// Pieces shared by the Material Requests, Material Inward, Tank Register and
// Suppliers tabs.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Loader2, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/shared/utils/utils';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import {
  useMaterialItemSearch, useSuppliers, useSaveSupplier, materialErrorText,
  type MaterialItem, type Supplier,
} from '../hooks/useMaterials';

export const fmtQty = (n: number | null | undefined) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }));

/** "22 Oct 2026" from a YYYY-MM-DD key or an ISO time. */
export function fmtDay(value: string | null | undefined) {
  if (!value) return '—';
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
export function fmtDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export type PillTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
const PILL: Record<PillTone, string> = {
  neutral: 'border-border bg-surface-container-low text-on-surface-variant',
  primary: 'border-primary/25 bg-primary-container text-primary-dim',
  success: 'border-success/25 bg-success/10 text-success-text',
  warning: 'border-warning/25 bg-warning/10 text-warning-text',
  danger: 'border-danger/25 bg-danger/10 text-danger-text',
  info: 'border-info/25 bg-info/10 text-info-text',
};
export function Pill({ tone = 'neutral', children, className, title }: { tone?: PillTone; children: ReactNode; className?: string; title?: string }) {
  return <span title={title} className={cn('inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-label-caps font-semibold', PILL[tone], className)}>{children}</span>;
}

export function Panel({ title, subtitle, actions, children, className }: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('overflow-hidden rounded-card border border-border bg-card shadow-surface-1', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface-container-low/60 px-5 py-3">
          <div className="min-w-0">
            {title && <h3 className="text-body-md font-semibold text-on-surface">{title}</h3>}
            {subtitle && <p className="text-body-sm text-on-surface-variant">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function FieldLabel({ children, required }: { children: ReactNode; required?: boolean }) {
  return <span className="mb-1 block text-body-sm font-medium text-on-surface">{children}{required && <span className="text-danger-text"> *</span>}</span>;
}

/** A tab strip of filter chips with counts. */
export function FilterChips<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string; count?: number }[] }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-body-sm font-semibold transition-colors',
            value === o.value ? 'border-primary bg-primary text-on-primary' : 'border-border bg-card text-on-surface-variant hover:border-primary/30 hover:text-on-surface',
          )}
        >
          {o.label}
          {o.count !== undefined && (
            <span className={cn('rounded-full px-1.5 text-[11px] tabular-nums', value === o.value ? 'bg-white/20' : 'bg-surface-container-high')}>{o.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Item search over the Item Master, showing stock on hand. Typing a name that
 * is not in the list keeps it as free text (productId null).
 */
/**
 * Item name, typed by hand — no search list (client, 2026-10-24: "search nahi
 * karenge, manually type karke add karenge"). The server matches the name to
 * the item already in the list, ignoring case and spaces, or adds it as a new
 * item (src/api-inventory/material-resolve.js).
 */
export function ItemNameInput({
  value, onChange, placeholder = 'Type the item name', autoFocus,
}: {
  value: string;
  onChange: (next: { itemName: string; productId: null }) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      className="h-9 w-full rounded-lg border border-input bg-surface px-3 text-body-sm text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      value={value}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange({ itemName: e.target.value, productId: null })}
    />
  );
}

/** Item picker with search — only for an Admin linking an older inward entry to an item. */
export function ItemSearchInput({
  value, productId, onChange, placeholder = 'Search item…', autoFocus,
}: {
  value: string;
  productId: string | null;
  onChange: (next: { itemName: string; productId: string | null; item?: MaterialItem }) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value);
  const [debounced, setDebounced] = useState(value);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setQ(value); }, [value]);
  useEffect(() => { const t = setTimeout(() => setDebounced(q), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const { data: items = [], isFetching } = useMaterialItemSearch(open ? debounced : '');

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" aria-hidden />
        <input
          className="h-9 w-full rounded-lg border border-input bg-surface pl-8 pr-8 text-body-sm text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          value={q}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); onChange({ itemName: e.target.value, productId: null }); }}
        />
        {productId ? <Check className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-success-text" aria-label="From the Item Master" />
          : isFetching ? <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-on-surface-subtle" /> : null}
      </div>
      {open && debounced.trim().length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-72 w-full min-w-[320px] overflow-auto rounded-lg border border-border bg-card shadow-surface-2">
          {items.length === 0 && !isFetching && (
            <div className="px-3 py-2.5 text-body-sm text-on-surface-variant">Not in the list yet — <b className="font-semibold text-on-surface">"{debounced.trim()}"</b> will be saved as a new item when you save.</div>
          )}
          {items.map((it) => (
            <button
              key={it._id}
              type="button"
              className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-surface-container-low"
              onClick={() => { onChange({ itemName: it.name, productId: it._id, item: it }); setQ(it.name); setOpen(false); }}
            >
              <span className="min-w-0">
                <span className="block truncate text-body-sm font-medium text-on-surface">{it.name}</span>
                {it.sku && <span className="block text-data-xs text-on-surface-subtle">{it.sku}</span>}
              </span>
              <span className={cn('shrink-0 font-mono text-data-xs tabular-nums', it.onHand > 0 ? 'text-success-text' : 'text-on-surface-subtle')}>
                {fmtQty(it.onHand)} in stock
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Supplier picker from the master, with "Add supplier" for a new one. */
export function SupplierPicker({ value, onChange }: { value: string; onChange: (s: Supplier | null) => void }) {
  const { data: suppliers = [] } = useSuppliers();
  const [adding, setAdding] = useState(false);
  const options = useMemo(() => suppliers.map((s) => ({
    value: s._id,
    label: s.name,
    description: [s.city, s.aliases.length ? `also: ${s.aliases.slice(0, 3).join(', ')}` : ''].filter(Boolean).join(' · '),
  })), [suppliers]);
  return (
    <div className="flex items-end gap-2">
      <div className="min-w-0 flex-1">
        <SearchableSelect
          options={options}
          value={value}
          onChange={(id) => onChange(suppliers.find((s) => s._id === id) || null)}
          placeholder="Choose supplier"
          searchPlaceholder="Search name or other spelling…"
          emptyText="No supplier matches — add it"
        />
      </div>
      <Button type="button" variant="outline" size="md" onClick={() => setAdding(true)} title="Add a supplier">
        <Plus className="h-4 w-4" /> New
      </Button>
      <AddSupplierModal open={adding} onClose={() => setAdding(false)} onAdded={(s) => { onChange(s); setAdding(false); }} />
    </div>
  );
}

export function AddSupplierModal({ open, onClose, onAdded }: { open: boolean; onClose: () => void; onAdded: (s: Supplier) => void }) {
  const save = useSaveSupplier();
  const [form, setForm] = useState({ name: '', phone: '', gstNumber: '', city: '' });
  useEffect(() => { if (open) setForm({ name: '', phone: '', gstNumber: '', city: '' }); }, [open]);
  const submit = async () => {
    try {
      const s = await save.mutateAsync(form);
      toast.success(`Added ${s.name}`);
      onAdded(s);
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not add the supplier.'));
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Add supplier" size="sm" footer={(
      <>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={save.isPending} disabled={form.name.trim().length < 2}>Add supplier</Button>
      </>
    )}>
      <div className="space-y-3">
        <Input label="Supplier name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus placeholder="e.g. ARIHANT LUBRICANTS" />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </div>
        <Input label="GSTIN" value={form.gstNumber} onChange={(e) => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })} maxLength={15} />
        <p className="text-body-sm text-on-surface-variant">A name that matches an existing supplier, or any of its other spellings, is refused so the list stays clean.</p>
      </div>
    </Modal>
  );
}
